import * as fabric from 'fabric';

export type PixelStrokeOwnProps = {
  color: string;
  width: number;
};

const pixelStrokeDefaults: PixelStrokeOwnProps = {
  color: '#000000',
  width: 0,
};

/** Number of evenly-spaced offsets used for the dilation pass. Higher
 *  = smoother stroke ring at higher CPU/GPU cost. 16 is a good middle
 *  ground for typical stroke widths up to ~32px. WebGL 1.0 requires
 *  loop bounds to be compile-time constants, which is why this is
 *  baked into both the JS and GLSL paths. */
const STEPS = 16;

/** Fragment shader for the WebGL filter backend. Mirrors the 2D
 *  implementation: for each pixel, if alpha > 0 keep the original
 *  color; otherwise sample STEPS points around a circle of radius
 *  `uStrokeWidth` (in source pixels) and emit the stroke color if any
 *  neighbor is opaque. Texel-space offset uses the auto-bound `uStepW`
 *  / `uStepH` (1 / source dimensions). */
const fragmentSource = `
  precision highp float;
  uniform sampler2D uTexture;
  uniform float uStepW;
  uniform float uStepH;
  uniform vec4 uStrokeColor;
  uniform float uStrokeWidth;
  varying vec2 vTexCoord;
  void main() {
    vec4 cur = texture2D(uTexture, vTexCoord);
    if (cur.a > 0.0) {
      gl_FragColor = cur;
      return;
    }
    for (int i = 0; i < ${STEPS}; i++) {
      float angle = float(i) / float(${STEPS}) * 6.28318531;
      vec2 offset = vec2(cos(angle) * uStepW, sin(angle) * uStepH) * uStrokeWidth;
      vec4 s = texture2D(uTexture, vTexCoord + offset);
      if (s.a > 0.0) {
        gl_FragColor = uStrokeColor;
        return;
      }
    }
    gl_FragColor = vec4(0.0);
  }
`;

/** Parse a CSS hex color (`#rrggbb` or `#rrggbbaa`) into normalized
 *  RGBA channels in 0..1. Returns black opaque on malformed input —
 *  the slider UI never produces malformed values, so this is purely
 *  defensive. */
function hexToRgba01(color: string): [number, number, number, number] {
  const hex = color.replace('#', '');
  if (hex.length === 6) {
    return [
      parseInt(hex.substring(0, 2), 16) / 255,
      parseInt(hex.substring(2, 4), 16) / 255,
      parseInt(hex.substring(4, 6), 16) / 255,
      1,
    ];
  }
  if (hex.length === 8) {
    return [
      parseInt(hex.substring(0, 2), 16) / 255,
      parseInt(hex.substring(2, 4), 16) / 255,
      parseInt(hex.substring(4, 6), 16) / 255,
      parseInt(hex.substring(6, 8), 16) / 255,
    ];
  }
  return [0, 0, 0, 1];
}

/** Pixel-aware stroke filter for Fabric `FabricImage` objects. Walks
 *  the image's alpha channel and paints a stroke ring along the
 *  visible-pixel boundary, instead of around the bounding rectangle
 *  (Fabric's native `.stroke` behavior).
 *
 *  Why this exists: paint-surface layers and PNG cutouts are stored
 *  as FabricImages whose backing canvas is mostly transparent. Native
 *  stroke would draw a rectangle around the whole image (essentially
 *  the canvas size), not around the painted/visible content. Pixel-
 *  aware stroke matches the user's mental model — outline what's
 *  visible.
 *
 *  Both pipelines run the same algorithm:
 *    - 2D backend: a "draw the source N times around a circle"
 *      dilation, recolor via `source-in`, knock out original alpha
 *      via `destination-out` to leave only the ring, composite under
 *      the original.
 *    - WebGL backend: per-pixel fragment shader samples STEPS points
 *      around a circle and outputs stroke color where the current
 *      pixel is transparent and a neighbor is opaque.
 *
 *  Both paths produce visually identical output. The filter is
 *  registered with `classRegistry` so Fabric's loadFromJSON can
 *  reconstruct it on session restore. */
export class PixelStrokeFilter extends fabric.filters.BaseFilter<
  'PixelStroke',
  PixelStrokeOwnProps
> {
  static type = 'PixelStroke' as const;
  static defaults = pixelStrokeDefaults;
  static uniformLocations = ['uStrokeColor', 'uStrokeWidth'];

  declare color: PixelStrokeOwnProps['color'];
  declare width: PixelStrokeOwnProps['width'];

  isNeutralState(): boolean {
    return !this.width || this.width <= 0;
  }

  /** WebGL pipeline entry point — return the GLSL fragment shader. */
  getFragmentSource(): string {
    return fragmentSource;
  }

  /** WebGL pipeline — push our custom uniforms each draw call. The
   *  base BaseFilter handles `uStepW` / `uStepH` automatically. */
  sendUniformData(
    gl: WebGLRenderingContext,
    uniformLocations: Record<string, WebGLUniformLocation | null>,
  ): void {
    const [r, g, b, a] = hexToRgba01(this.color);
    if (uniformLocations.uStrokeColor) {
      gl.uniform4f(uniformLocations.uStrokeColor, r, g, b, a);
    }
    if (uniformLocations.uStrokeWidth) {
      gl.uniform1f(uniformLocations.uStrokeWidth, this.width);
    }
  }

  /** 2D pipeline — used when WebGL is unavailable or disabled. */
  applyTo2d({ imageData }: { imageData: ImageData }): void {
    const w = this.width;
    if (!w || w <= 0) return;
    const { width, height } = imageData;
    if (width === 0 || height === 0) return;

    const source = document.createElement('canvas');
    source.width = width;
    source.height = height;
    const sCtx = source.getContext('2d');
    if (!sCtx) return;
    sCtx.putImageData(imageData, 0, 0);

    const ring = document.createElement('canvas');
    ring.width = width;
    ring.height = height;
    const rCtx = ring.getContext('2d');
    if (!rCtx) return;

    for (let i = 0; i < STEPS; i++) {
      const angle = (i / STEPS) * Math.PI * 2;
      const dx = Math.cos(angle) * w;
      const dy = Math.sin(angle) * w;
      rCtx.drawImage(source, dx, dy);
    }

    rCtx.globalCompositeOperation = 'source-in';
    rCtx.fillStyle = this.color;
    rCtx.fillRect(0, 0, width, height);

    rCtx.globalCompositeOperation = 'destination-out';
    rCtx.drawImage(source, 0, 0);

    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const oCtx = out.getContext('2d');
    if (!oCtx) return;
    oCtx.drawImage(ring, 0, 0);
    oCtx.drawImage(source, 0, 0);

    const final = oCtx.getImageData(0, 0, width, height);
    imageData.data.set(final.data);
  }

  toObject(): { type: 'PixelStroke'; color: string; width: number } {
    return {
      type: PixelStrokeFilter.type,
      color: this.color,
      width: this.width,
    };
  }
}

// Register so loadFromJSON can reconstruct the filter from its
// serialized form. Fabric uses the static `type` to look up
// constructors at deserialize time.
fabric.classRegistry.setClass(PixelStrokeFilter);

/** Sentinel so we can find / remove our filter later without confusing
 *  it with native filters. */
export const PIXEL_STROKE_TYPE = PixelStrokeFilter.type;
